// src/views/ProtectedCrmLayout.js
// PHASE 1.4 VERIFIER: r1-d72f3e08
import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from '../firebase/config';
import { doc, onSnapshot, collection, query, orderBy, updateDoc, where, writeBatch, getDoc } from 'firebase/firestore';
import MainLayout from '../components/layout/MainLayout';
import CrmApp from './CrmApp';
import AuthPage from './AuthPage';
import ExpensesView from './ExpensesView';
import JobProfitabilityDetailView from './JobProfitabilityDetailView';
import EmailCampaignsView from './EmailCampaignsView'; 
import { getNotificationRoute } from '../constants/notificationRouting';

// --- IMPORTS ---
import UsersManagement from '../components/crm/UsersManagement';
import HiringView from './HiringView';
import RoleProtectedRoute from '../components/common/RoleProtectedRoute'; 
import ProfitabilityReportView from './reports/ProfitabilityReportView'; 
import ProfitLossReportView from './reports/ProfitLossReportView'; 
import SalesReportView from './reports/SalesReportView'; 
import OverdueReportView from './reports/OverdueReportView';
import OverdueJobsReportView from './reports/OverdueJobsReportView';
import CancellationReportView from './reports/CancellationReportView';
import TeamReportView from './reports/TeamReportView';
import CollectionsReportView from './reports/CollectionsReportView';
import StaleAccountCreditsReportView from './reports/StaleAccountCreditsReportView'; // stalecred-c5a41e92 (Seam 13)
import EstimatesReportView from './reports/EstimatesReportView'; // qreport-5b8e2c64 (Quote Seam 12a)
import { ESTIMATES_REPORT_FULL_ROLES, ESTIMATES_REPORT_SCOPED_ROLES } from '../utils/quoteReporting'; // qreport-5b8e2c64
import { canSeeAllCustomers } from '../utils/permissions'; // s9leads-2a7c5e31 — new-lead badge
import DeviceTzWarning from '../components/common/DeviceTzWarning'; // tzcanada-9b4e2f17

const ProtectedCrmLayout = () => {
    const [user, setUser] = useState(null);
    const [currentUserProfile, setCurrentUserProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [newLeadCount, setNewLeadCount] = useState(0); 
    // PHASE 1.6 VERIFIER: r1-16-web-sidebar-badge-16a3f2b8
    const [newFlaggedCount, setNewFlaggedCount] = useState(0);
    const [companyTz, setCompanyTz] = useState(null); // tzcanada-9b4e2f17 — for the device time-zone warning
    const navigate = useNavigate();
    
    // --- DEFINE ALLOWED ROLES CONSTANT ---
    // This ensures we use the exact same list for every route
    const ALL_ADMIN_ROLES = ['Admin', 'Manager', 'Owner/System Admin', 'Owner / System Admin', 'Owner', 'System Admin'];

    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUser(user);
            } else {
                setUser(null);
                setCurrentUserProfile(null);
                setNotifications([]);
                setIsLoading(false);
            }
        });
        return () => unsubscribeAuth();
    }, []);

    // --- STABLE HOOKS ---
    useEffect(() => {
        if (!user) {
            if (!isLoading) setIsLoading(false);
            return;
        }

        const userDocRef = doc(db, 'users', user.uid);

        // emailrecon-7081920a — run the Auth↔mirror email reconcile at most once per
        // mounted session (the mirror snapshot below fires repeatedly; a closure flag
        // keeps reload() from firing on every profile change).
        let emailReconcileAttempted = false;

        // Helper — builds and sets the user profile, optionally overriding subscription fields.
        // Kept outside the callback so it's synchronous and never async.
        const applyProfile = (userDoc, subscriptionStatus, planTier) => {
            const d = userDoc.data();
            setCurrentUserProfile(prevProfile => {
                const newProfile = {
                    id: userDoc.id,
                    uid: d.uid || userDoc.id,
                    name: d.name,
                    email: d.email,
                    role: d.role,
                    organizationId: d.organizationId,
                    organizationName: d.organizationName || null,
                    avatarUrl: d.avatarUrl || null,
                    subscriptionStatus,
                    planTier,
                    isSuperAdmin: d.isSuperAdmin || false,
                    stripeCustomerId: d.stripeCustomerId || null,
                    onboardingCompleted: d.onboardingCompleted || false,
                    trialEnd: d.trialEnd || null, // [trial-countdown] carry Firestore Timestamp through to BillingSettings
                    columnPrefs: d.columnPrefs || null, // colprefs-8c2f4a91 -- per-user table column visibility (Seam 2); additive, unchanged value dedups cleanly
                };
                if (JSON.stringify(prevProfile) === JSON.stringify(newProfile)) {
                    return prevProfile;
                }
                return newProfile;
            });
        };

        const unsubscribeProfile = onSnapshot(userDocRef,
            (userDoc) => {
                if (!userDoc.exists()) {
                    setIsLoading(false);
                    return;
                }
                const d = userDoc.data();

                // emailrecon-8192a0b1 — reconcile a completed self-service email change.
                // When the user confirms an email change via Firebase's verification link,
                // Auth updates but the users/{uid} mirror doesn't. Here we reload the Auth
                // user and, only on a genuine mismatch, heal the mirror. This is strictly
                // fire-and-forget: it is NOT awaited, so the synchronous setIsLoading(false)
                // calls below (and therefore first render) never wait on it, and any failure
                // is swallowed and logged — it can never surface to the user or throw.
                if (!emailReconcileAttempted) {
                    emailReconcileAttempted = true;
                    const mirrorEmail = d.email;
                    (async () => {
                        try {
                            const authUser = auth.currentUser;
                            if (!authUser) return;
                            await authUser.reload();
                            const authEmail = auth.currentUser?.email;
                            if (authEmail && authEmail !== mirrorEmail) {
                                await updateDoc(userDocRef, { email: authEmail });
                            }
                        } catch (reconErr) {
                            console.warn('email reconcile skipped:', reconErr);
                        }
                    })();
                }

                const isOwnerRole = ['Owner', 'Owner/System Admin', 'System Admin', 'Admin'].includes(d.role);
                const subscriptionStatus = d.subscriptionStatus || null;
                const planTier = d.planTier || null;

                if (isOwnerRole && !subscriptionStatus && d.organizationId) {
                    // Secondary owner — look up the org's original owner subscription status.
                    // Use .then() chains to stay synchronous from Firestore's perspective.
                    getDoc(doc(db, 'organizations', d.organizationId))
                        .then(orgSnap => {
                            if (!orgSnap.exists()) return Promise.resolve({ status: null, tier: null });
                            const ownerUid = orgSnap.data().ownerUid;
                            if (!ownerUid || ownerUid === userDoc.id) return Promise.resolve({ status: null, tier: planTier });
                            return getDoc(doc(db, 'users', ownerUid)).then(ownerSnap => ({
                                status: ownerSnap.exists() ? (ownerSnap.data().subscriptionStatus || null) : null,
                                tier:   ownerSnap.exists() ? (ownerSnap.data().planTier || planTier) : planTier,
                            }));
                        })
                        .then(({ status, tier }) => {
                            applyProfile(userDoc, status, tier);
                            setIsLoading(false);
                        })
                        .catch(() => {
                            applyProfile(userDoc, null, planTier);
                            setIsLoading(false);
                        });
                    // Don't call setIsLoading here — it's called inside .then()
                    return;
                }

                // Fast path — user has their own subscription status
                applyProfile(userDoc, subscriptionStatus, planTier);
                setIsLoading(false);
            },
            (error) => {
                console.error("CRITICAL: Failed to load user profile", error);
                setIsLoading(false);
            }
        );

        const notificationsRef = collection(db, 'users', user.uid, 'notifications');
        const qNotes = query(notificationsRef, orderBy('createdAt', 'desc'));
        const unsubscribeNotifications = onSnapshot(qNotes, (snapshot) => {
            const notificationsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setNotifications(notificationsData); 
        });
        
        return () => {
            unsubscribeProfile();
            unsubscribeNotifications();
        };
    }, [user?.uid]);

    // --- SMART LEAD COUNTING LOGIC ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) {
            setNewLeadCount(0); 
            return;
        }

        const orgId = currentUserProfile.organizationId;
        const requestsRef = collection(db, `organizations/${orgId}/requests`);
        
        const qLeads = query(requestsRef, where("status", "==", "New"));
        
        const unsubscribeLeads = onSnapshot(qLeads, (snapshot) => {
            const allNewLeads = snapshot.docs.map(doc => doc.data());
            
            const myRelevantLeads = allNewLeads.filter(lead => {
                // s9leads-2a7c5e31 — the badge counts exactly what the Leads list shows: everyone who
                // sees every request (Office Staff + every high-level spelling, canSeeAllCustomers —
                // the RequestsView rule since S9b) counts every new lead; others count theirs + unassigned.
                if (canSeeAllCustomers({ role: currentUserProfile.role })) { // role only — matches this effect's deps
                    return true;
                }

                const assignedToMe = lead.assignedTo === currentUserProfile.id;
                const isUnassigned = !lead.assignedTo; 

                return assignedToMe || isUnassigned;
            });

            setNewLeadCount(myRelevantLeads.length); 
        });

        return () => {
            unsubscribeLeads(); 
        };
    }, [currentUserProfile?.organizationId, currentUserProfile?.role, currentUserProfile?.id]);


    // PHASE 1.6 VERIFIER: r1-16-web-sidebar-badge-16a3f2b8
    // ── FLAGGED-NOTE COUNTING (for sidebar badge) ────────────────────────
    // Mirrors the same query the Flagged Inbox view uses, minus the orderBy
    // (we only need the count, not the ordering). Same composite index
    // built in sub-batch 1 covers this — Firestore can use it without the
    // orderBy clause, or intersect single-field indexes if not.
    //
    // Permission posture: firestore.rules grants any org member read on
    // notes; the query filters to flagTargetUserId == currentUid, so the
    // listener only sees the current user's flags. Defense-in-depth.
    //
    // FlaggedInbox.js subscribes to the SAME query independently. Two
    // listeners on identical queries is intentional — keeps the sidebar
    // badge live across the whole app (not just while inbox is mounted)
    // and the inbox view self-sufficient. Firestore dedupes at the wire.
    useEffect(() => {
        if (!currentUserProfile?.organizationId || !currentUserProfile?.id) {
            setNewFlaggedCount(0);
            return undefined;
        }

        const orgId = currentUserProfile.organizationId;
        const uid = currentUserProfile.id;
        const notesRef = collection(db, `organizations/${orgId}/notes`);
        const qFlags = query(
            notesRef,
            // FO-4 multi-reviewer: bell/sidebar count on the reviewer group.
            where('flagReviewerIds', 'array-contains', uid),
            where('flagged', '==', true),
            where('resolved', '==', false),
        );

        const unsubscribeFlags = onSnapshot(
            qFlags,
            (snapshot) => {
                setNewFlaggedCount(snapshot.size);
            },
            (err) => {
                // Common cause: composite index still building. Logged but
                // not surfaced — sidebar simply shows no badge until the
                // listener recovers. Inbox view surfaces the error UX.
                console.warn('ProtectedCrmLayout: flagged-count listener error:', err);
                setNewFlaggedCount(0);
            },
        );

        return () => {
            unsubscribeFlags();
        };
    }, [currentUserProfile?.organizationId, currentUserProfile?.id]);


    // tzcanada-9b4e2f17 — the company's zone, for DeviceTzWarning. The same org doc CrmApp listens
    // to (Firestore shares one listener per document). Any member can read it.
    useEffect(() => {
        if (!currentUserProfile?.organizationId) { setCompanyTz(null); return undefined; }
        const unsub = onSnapshot(doc(db, 'organizations', currentUserProfile.organizationId),
            (d) => setCompanyTz(d.exists() ? (d.data().timezone || null) : null),
            () => setCompanyTz(null));
        return () => unsub();
    }, [currentUserProfile?.organizationId]);

    const handleLogout = useCallback(async () => {
        await signOut(auth);
        navigate('/');
    }, [navigate]);

    const handleNotificationClick = useCallback(async (notification) => {
        // PHASE 1.4 VERIFIER: r1-d72f3e08
        // Mark read, then look up the deep-link path from the central
        // routing map. Returns null for note_reminder (no destination yet)
        // and for malformed notifications missing required IDs.
        if (!notification.isRead && user && user.uid) {
            try {
                const notificationRef = doc(db, 'users', user.uid, 'notifications', notification.id);
                await updateDoc(notificationRef, { isRead: true });
            } catch (error) {
                console.error("Error marking notification read:", error);
            }
        }

        const path = getNotificationRoute(notification);
        if (path) {
            navigate(path);
            return;
        }

        // Legacy fallback — older notifications may have a hardcoded `link`
        // field. New writers don't populate it. Kept for backward compat
        // until the legacy notification docs roll off (typically <30 days).
        if (notification.link) {
            navigate(notification.link);
        }
    }, [user, navigate]);

    const handleMarkAllAsRead = useCallback(async () => {
        if (!user || !notifications.length) return;

        const unreadNotes = notifications.filter(n => !n.isRead);
        if (unreadNotes.length === 0) return;

        try {
            const batch = writeBatch(db);
            unreadNotes.forEach(note => {
                const ref = doc(db, 'users', user.uid, 'notifications', note.id);
                batch.update(ref, { isRead: true });
            });
            await batch.commit();
        } catch (error) {
            console.error("Error marking all as read:", error);
        }
    }, [user, notifications]);

    // PHASE 1.4 VERIFIER: r1-5b8e9f02
    // handleMarkFlagDone REMOVED. The old handler force-completed the job
    // referenced by notification.entityId. Pre-1.4, that entityId was always
    // a follow-up Job, so completion was safe. Post-1.4, entityId can be the
    // ORIGINAL job (when no follow-up was scheduled), and completing that
    // job would discard real work. The Mark Done button in NotificationsPanel
    // is also removed. Follow-up completion now happens via the standard
    // JobFormModal flow only — open from calendar/kanban, set Completed,
    // fill the completion-note prompt, save. Re-introducing a one-click
    // shortcut would require a dedicated 'follow_up_due' notification type
    // whose entityId is guaranteed to point at a follow-up; tracked as a
    // backlog item.
    
    if (isLoading) {
        return (
            <div className="h-screen flex flex-col items-center justify-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 text-sm">Loading workspace...</p>
            </div>
        );
    }

    if (!user) {
        return <AuthPage />; 
    }

    if (user && currentUserProfile) {
        return (
            <MainLayout 
                user={user} 
                currentUserProfile={currentUserProfile}
                onLogout={handleLogout}
                notifications={notifications}
                onNotificationClick={handleNotificationClick}
                onMarkAllRead={handleMarkAllAsRead}
                newLeadCount={newLeadCount}
                newFlaggedCount={newFlaggedCount} /* PHASE 1.6 VERIFIER: r1-16-web-sidebar-badge-16a3f2b8 */
            >
                {/* --- Onboarding Tour --- */}
                <DeviceTzWarning companyTz={companyTz} />

                <Routes>
                    <Route path="/expenses" element={<ExpensesView currentUserProfile={currentUserProfile} />} />
                    
                    <Route 
                        path="/email-marketing" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <EmailCampaignsView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />
                    
                    <Route 
                        path="/reports/job/:jobId" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <JobProfitabilityDetailView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />
                    
                    <Route 
                        path="/reports/profitability" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <ProfitabilityReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route 
                        path="/reports/profit-loss" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <ProfitLossReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route 
                        path="/reports/sales" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <SalesReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    {/* qreport-5b8e2c64 — the ONE report a Sales Consultant may open:
                        the helper narrows it to estimates where they are the
                        salesperson (display-level only; rules unchanged). */}
                    <Route
                        path="/reports/estimates"
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={[...ESTIMATES_REPORT_FULL_ROLES, ...ESTIMATES_REPORT_SCOPED_ROLES]}>
                                <EstimatesReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        }
                    />

                    <Route
                        path="/reports/overdue"
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <OverdueReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route
                        path="/reports/collections"
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <CollectionsReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        }
                    />

                    <Route
                        path="/reports/stale-credits"
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <StaleAccountCreditsReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        }
                    />

                    <Route 
                        path="/reports/overdue-jobs" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <OverdueJobsReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route 
                        path="/reports/cancellations" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <CancellationReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route 
                        path="/hiring" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <HiringView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route 
                        path="/employees" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <UsersManagement currentUser={user} currentUserProfile={currentUserProfile} /> 
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route 
                        path="/reports/team" 
                        element={
                            <RoleProtectedRoute currentUserProfile={currentUserProfile} allowedRoles={ALL_ADMIN_ROLES}>
                                <TeamReportView currentUserProfile={currentUserProfile} />
                            </RoleProtectedRoute>
                        } 
                    />

                    <Route path="*" element={<CrmApp user={user} currentUserProfile={currentUserProfile} />} />
                </Routes>
            </MainLayout>
        );
    }

    return <div className="h-screen flex items-center justify-center"><p>Loading workspace...</p></div>;
};

export default ProtectedCrmLayout;